import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { getJSON, patchJSON, postJSON } from '@/lib/api'
import type {
  ConnectionDetail,
  ConnectionType,
  SyncRun,
  RecordError,
  ConnectionAudit,
} from './detailTypes'

export function useConnectionDetail(id: number) {
  const client = useQueryClient()
  const path = `/api/v1/connections/${id}`
  const connection = useQuery({
    queryKey: ['connections', id],
    queryFn: () => getJSON<{ connection: ConnectionDetail }>(path),
  })
  const types = useQuery({
    queryKey: ['connection-types'],
    queryFn: () => getJSON<{ connection_types: ConnectionType[] }>('/api/v1/connection_types'),
  })
  const runs = useQuery({
    queryKey: ['connections', id, 'runs'],
    queryFn: () => getJSON<{ sync_runs: SyncRun[] }>(`${path}/sync_runs`),
    refetchInterval: 15000,
  })
  useEffect(() => {
    if (!runs.data) return
    void client.invalidateQueries({ queryKey: ['connections', id], exact: true })
    void client.invalidateQueries({ queryKey: ['connections', id, 'record_errors'] })
  }, [client, id, runs.data])
  return { connection, types, runs }
}
export function useConnectionHistory(id: number, kind: string, query = '') {
  return useQuery({
    queryKey: ['connections', id, kind, query],
    queryFn: () =>
      getJSON<{ record_errors: RecordError[]; audits: ConnectionAudit[] }>(
        `/api/v1/connections/${id}/${kind}${query}`,
      ),
  })
}
export type DetailCommand = { action: string; attributes?: Record<string, unknown> }
export function useDetailAction(id: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ action, attributes }: DetailCommand) => {
      const path = `/api/v1/connections/${id}`
      if (attributes) return patchJSON(path, { connection: attributes })
      return postJSON(`${path}/${action}`, {})
    },
    onSuccess: () => client.invalidateQueries({ queryKey: ['connections'] }),
  })
}
export async function reconnectConnection(id: number) {
  const result = await postJSON<{ connection_oauth_authorization: { authorization_url: string } }>(
    '/api/v1/connection_oauth_authorizations',
    { connection_id: id },
  )
  window.location.assign(result.connection_oauth_authorization.authorization_url)
}
